import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ClickAwayListener,
  Collapse,
} from '@mui/material';
import Picker, { IEmojiData } from 'emoji-picker-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlacePrediction } from '../../../hooks/usePlacesAutocomplete';
import { useToggleSet } from '../../../hooks/useToggleSet';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectListOrder, selectOrderedTopLevelItems } from '../../../redux/trip/trip.selectors';
import {
  addListToDivider,
  removeDivider,
  updateActiveListId,
  updateDivider,
} from '../../../redux/trip/trip.slice';
import { Trip, TripDivider, TripList } from '../../../redux/trip/trip.types';
import { getOrderedCards, getOrderedListsForDivider } from '../../../redux/trip/trip.utils';
import { selectCurrentUser } from '../../../redux/user/user.selectors';
import SortableCard from '../../Card/SortableCard';
import PlacesAutocomplete from '../../PlacesAutocomplete/PlacesAutocomplete';
import AddListInput from './components/AddListInput/AddListInput';
import ItemOptionsMenu from './components/ItemOptionsMenu/ItemOptionsMenu';
import ListItem from './components/ListItem/ListItem';
import RenameListInput from './components/RenameListInput/RenameListInput';
import { SidebarUIContext, useSidebarUI } from './SidebarUIContext';
import '../Itinerary/components/ItinerarySidebar.scss';
import './TripSidebar.scss';

const DEFAULT_LIST_EMOJI = '🏝️';
const MAX_CHARACTER_COUNT = 100;
const CANCEL_TIMEOUT = 100;

interface TripSidebarMobileProps {
  trip: Trip;
  canEdit?: boolean;
  location?: { lat: number; lng: number };
  onSuggestionMouseEnter?: (suggestion: PlacePrediction) => void;
  onSuggestionMouseLeave?: (suggestion: PlacePrediction) => void;
  onSuggestionSelect?: (
    suggestion: PlacePrediction,
    sessionToken: string,
    usedNewApi: boolean
  ) => void;
}

// ─── MobileDividerSection ────────────────────────────────────────────────────
// Manages the per-divider header UI: collapse toggle, + add list, ••• options.
// Extracted so each divider owns its own state (isAddingList, isEditingName, etc.)

interface MobileDividerSectionProps {
  tripId: string;
  divider: TripDivider;
  tripListOrder: string[];
  trip: Trip;
  isOpen: boolean;
  onToggle: () => void;
  canEdit: boolean;
  children: React.ReactNode;
}

function MobileDividerSection({
  tripId,
  divider,
  tripListOrder,
  trip,
  isOpen,
  onToggle,
  canEdit,
  children,
}: MobileDividerSectionProps) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const nestedLists = useMemo(() => getOrderedListsForDivider(trip, divider), [trip, divider]);
  const { openEmojiId, setOpenEmojiId } = useSidebarUI();
  const isEditingEmoji = openEmojiId === divider.id;

  const [isAddingList, setIsAddingList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const anchorElRef = useRef<HTMLDivElement | null>(null);
  const cancelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // opens the ••• options menu
  const handleOptionsOpen = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    anchorElRef.current = e.currentTarget;
    setAnchorEl(e.currentTarget);
  }, []);

  // shows the inline add-list input
  const showAddList = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isOpen) {
        onToggle();
      }
      setIsAddingList(true);
    },
    [isOpen, onToggle]
  );

  // saves the new list into this divider
  const onAddList = useCallback(() => {
    setIsAddingList(false);
    if (tripId && currentUser && newListName.trim().length > 0) {
      dispatch(
        addListToDivider({
          tripId,
          dividerId: divider.id,
          list: {
            name: newListName.trim(),
            emoji: DEFAULT_LIST_EMOJI,
            creator: currentUser.uid,
          },
          dividerListOrder: divider.listOrder,
        })
      );
      setNewListName('');
    }
  }, [tripId, currentUser, newListName, divider.id, divider.listOrder, dispatch]);

  // cancels the add-list input with a short delay so a save click can register before blur fires
  const cancelAddList = useCallback(() => {
    if (cancelTimeoutRef.current) {
      clearTimeout(cancelTimeoutRef.current);
    }
    cancelTimeoutRef.current = setTimeout(() => {
      setIsAddingList(false);
      setNewListName('');
      cancelTimeoutRef.current = null;
    }, CANCEL_TIMEOUT);
  }, []);

  useEffect(() => {
    return () => {
      if (cancelTimeoutRef.current) {
        clearTimeout(cancelTimeoutRef.current);
      }
    };
  }, []);

  // handles Enter (save), Escape (cancel), and enforces the character limit while typing
  const handleAddListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      if (e.key === 'Enter' && target.value.trim().length > 0) {
        onAddList();
        return;
      }
      if (e.key === 'Escape') {
        cancelAddList();
        return;
      }
      if (
        target.value.length >= MAX_CHARACTER_COUNT &&
        e.key !== 'Backspace' &&
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowRight'
      ) {
        e.preventDefault();
      }
    },
    [onAddList, cancelAddList]
  );

  const onSaveName = useCallback(
    (name: string) => {
      dispatch(updateDivider({ tripId, dividerId: divider.id, field: 'name', value: name }));
    },
    [dispatch, tripId, divider.id]
  );

  const handleDelete = useCallback(() => {
    const allCardIds = nestedLists.flatMap((l) => l.cardOrder ?? []);
    dispatch(
      removeDivider({
        tripId,
        dividerId: divider.id,
        tripListOrder,
        nestedListIds: divider.listOrder,
        allCardIds,
      })
    );
  }, [dispatch, tripId, divider.id, tripListOrder, divider.listOrder, nestedLists]);

  const handleDividerEmojiEdit = useCallback(
    (e: React.MouseEvent<HTMLLIElement>) => {
      e.stopPropagation();
      setAnchorEl(null);
      setTimeout(() => setOpenEmojiId(divider.id), 0);
    },
    [divider.id, setOpenEmojiId]
  );

  const onEmojiClick = useCallback(
    (_event: React.MouseEvent, emojiObject: IEmojiData) => {
      setOpenEmojiId(null);
      dispatch(
        updateDivider({ tripId, dividerId: divider.id, field: 'emoji', value: emojiObject.emoji })
      );
    },
    [dispatch, tripId, divider.id, setOpenEmojiId]
  );

  const handleEmojiClickAway = useCallback(() => setOpenEmojiId(null), [setOpenEmojiId]);

  return (
    <>
      <div
        className='mobile-divider-header'
        onClick={onToggle}
      >
        <ChevronRightRoundedIcon className={`mobile-divider-chevron${isOpen ? ' expanded' : ''}`} />

        {isEditingName ? (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ flex: 1 }}
          >
            <RenameListInput
              tripId={tripId}
              list={divider}
              canEdit={canEdit}
              isEditingName={isEditingName}
              setIsEditingName={setIsEditingName}
              isMobile
              onSave={onSaveName}
            />
          </div>
        ) : (
          <>
            <span className='mobile-divider-emoji'>{divider.emoji}</span>
            <span className='mobile-divider-name'>{divider.name}</span>
          </>
        )}

        {canEdit && !isEditingName && isOpen && (
          <div
            className='mobile-divider-actions'
            onClick={(e) => e.stopPropagation()}
          >
            {!isAddingList && (
              <div
                className='divider-add-btn mobile'
                onClick={showAddList}
              >
                +
              </div>
            )}
            <div
              className='list-options-div mobile'
              onClick={handleOptionsOpen}
            >
              •••
            </div>
          </div>
        )}
      </div>

      {isEditingEmoji && (
        <ClickAwayListener onClickAway={handleEmojiClickAway}>
          <div
            className='emoji-picker-div mobile'
            onClick={(e) => e.stopPropagation()}
          >
            <Picker onEmojiClick={onEmojiClick} />
          </div>
        </ClickAwayListener>
      )}

      {isAddingList && (
        <div className='mobile-divider-lists'>
          <div className='divider-add-list-input'>
            <input
              className='styled-input'
              placeholder='e.g. Restaurants'
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onBlur={cancelAddList}
              onKeyDown={handleAddListKeyDown}
              autoFocus
            />
            <div className='new-list-buttons'>
              <div
                className='cancel-add-item-div'
                onClick={cancelAddList}
              >
                <CloseRoundedIcon className='cancel-add-item-button' />
                <span className='cancel-span'>CANCEL</span>
              </div>
              {newListName.trim().length > 0 && (
                <div
                  className='add-item-div'
                  onClick={onAddList}
                >
                  <CheckRoundedIcon className='save-add-item-button' />
                  <span className='add-span'>SAVE</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Collapse
        in={isOpen}
        timeout={200}
        unmountOnExit
      >
        <div className='mobile-divider-lists'>{children}</div>
      </Collapse>

      <ItemOptionsMenu
        itemName={divider.name}
        isMobile
        anchorEl={anchorEl}
        setAnchorEl={() => setAnchorEl(null)}
        onRename={() => setIsEditingName(true)}
        onEmojiEdit={handleDividerEmojiEdit}
        onDelete={handleDelete}
        deleteMessage={
          <>
            Deleting <strong>{divider.name}</strong> will remove all lists and places inside it.
          </>
        }
      />
    </>
  );
}

// ─── TripSidebarMobile ───────────────────────────────────────────────────────

function TripSidebarMobile({
  trip,
  canEdit,
  location,
  onSuggestionMouseEnter,
  onSuggestionMouseLeave,
  onSuggestionSelect,
}: TripSidebarMobileProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const listOrder = useAppSelector(selectListOrder);
  const orderedTopLevelItems = useAppSelector(selectOrderedTopLevelItems);

  const [isAddingPlace, setIsAddingPlace] = useState(false);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  // tracks which dividers are expanded — all start collapsed
  const [expandedDividerIds, toggleDivider] = useToggleSet();
  // shared context for emoji pickers and options menus — ensures only one is open at a time
  const [openEmojiId, setOpenEmojiId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // find the first top-level list to auto-expand on load (dividers start closed, so skip them)
  const firstListId = useMemo(() => {
    for (const item of orderedTopLevelItems) {
      if (item.itemType === 'list') {
        return item.data.id;
      }
    }
    return false as const;
  }, [orderedTopLevelItems]);

  const [expanded, setExpanded] = useState<string | false>(firstListId);

  const currentActiveList = useMemo(() => {
    if (!expanded) {
      return undefined;
    }
    return trip.lists.find((list) => list.id === expanded);
  }, [expanded, trip.lists]);

  const handleListChange =
    (listId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? listId : false);
      if (isExpanded) {
        dispatch(updateActiveListId(listId));
        searchParams.set('list', listId);
      } else {
        searchParams.delete('list');
      }
      setSearchParams(searchParams);
    };

  useEffect(() => {
    setExpanded(searchParams.get('list') || false);
  }, [searchParams]);

  const renderListAccordion = (list: TripList, dividerId?: string) => (
    <React.Fragment key={list.id}>
      {canEdit && isAddingPlace && expanded === list.id && (
        <div className='mobile-places-input-container'>
          <div className='mobile-list-title-container'>
            <span className='mobile-list-title-emoji'>{list.emoji}</span>
            <p className='mobile-list-title'>{list.name}</p>
          </div>
          <PlacesAutocomplete
            location={location}
            placeholder='Add a place or an address'
            className='playground-autocomplete'
            onSuggestionMouseEnter={onSuggestionMouseEnter}
            onSuggestionMouseLeave={onSuggestionMouseLeave}
            onSuggestionSelect={onSuggestionSelect}
            setIsAddingPlace={setIsAddingPlace}
            shouldClearValueOnSelect
            isMobilePlayground
            activeList={currentActiveList}
            cards={trip.cards}
          />
        </div>
      )}
      {!isAddingPlace && (
        <Accordion
          className='itinerary-sidebar-accordion'
          expanded={expanded === list.id}
          onChange={handleListChange(list.id)}
          disableGutters
          slotProps={{ transition: { timeout: 300, unmountOnExit: false } }}
        >
          <AccordionSummary className='itinerary-sidebar-item-accordion-summary'>
            <ListItem
              tripId={trip.id}
              list={list}
              listOrder={
                dividerId
                  ? (trip.dividers.find((d) => d.id === dividerId)?.listOrder ?? [])
                  : listOrder || []
              }
              dividerId={dividerId}
              isActive={expanded === list.id}
              canEdit={canEdit}
              isEditingName={list.id === editingListId}
              onStartEditing={() => setEditingListId(list.id)}
              onFinishEditing={() => setEditingListId(null)}
              cardCount={list.cardOrder?.length || 0}
            />
          </AccordionSummary>
          <AccordionDetails>
            {canEdit && (
              <div
                className='list-input-btn'
                onClick={() => {
                  setIsAddingPlace(true);
                  dispatch(updateActiveListId(list.id));
                }}
              >
                <img
                  className='list-input-icon'
                  src='/svgs/marker.svg'
                  alt='marker'
                />
                <input
                  className='list-input-button'
                  placeholder='Add a place or an address '
                  disabled
                />
              </div>
            )}
            <div className='itinerary-cards-container'>
              <SortableContext
                items={list.cardOrder ?? []}
                strategy={verticalListSortingStrategy}
              >
                {expanded === list.id &&
                  getOrderedCards(trip, list).map((card) => (
                    <SortableCard
                      key={card.id}
                      card={card}
                      listId={list.id}
                      tripId={trip.id}
                      cardOrder={list.cardOrder ?? []}
                      canEdit={canEdit}
                    />
                  ))}
              </SortableContext>
            </div>
          </AccordionDetails>
        </Accordion>
      )}
    </React.Fragment>
  );

  return (
    <SidebarUIContext.Provider value={{ openEmojiId, setOpenEmojiId, openMenuId, setOpenMenuId }}>
      {!isAddingPlace && (
        <AddListInput
          tripId={trip.id}
          canEdit={canEdit}
          listOrder={listOrder}
        />
      )}
      {orderedTopLevelItems.map((item) => {
        if (item.itemType === 'list') {
          return renderListAccordion(item.data);
        }
        // divider: collapsible section with header actions and animated nested lists
        const isOpen = expandedDividerIds.has(item.data.id);
        const nestedLists = getOrderedListsForDivider(trip, item.data);
        return (
          <MobileDividerSection
            key={item.data.id}
            tripId={trip.id}
            divider={item.data}
            tripListOrder={listOrder || []}
            trip={trip}
            isOpen={isOpen}
            onToggle={() => toggleDivider(item.data.id)}
            canEdit={canEdit ?? false}
          >
            {nestedLists.map((list) => renderListAccordion(list, item.data.id))}
          </MobileDividerSection>
        );
      })}
    </SidebarUIContext.Provider>
  );
}

TripSidebarMobile.displayName = 'TripSidebarMobile';
export default TripSidebarMobile;
